<template>
	<div data-tour-scope="setting-currency">
		<v-main>
			<v-container fluid fill-height>
				<v-layout justify-center align-center v-resize="onResize">
					<v-card width="100%" outlined elevation-0>
						<v-toolbar flat class="border_bottom" shaped>
							<v-toolbar-title> {{ this.$vuetify.lang.t('$vuetify.front.subsetting.currencies') }} </v-toolbar-title>
							<v-btn class="ml-6" outlined color="primary" data-tour="tutorial" @click="tour.start()"><v-icon left>mdi-school-outline</v-icon>Tutorial guidato</v-btn>
							<v-spacer></v-spacer>
						</v-toolbar>

						<!-- riepilogo: valuta di base ed esempio di conversione con i cambi attuali -->
						<div v-if="base" class="cur-summary ma-3">
							<div class="cur-summary-flag">
								<img :src="bandiera(base.flag)" :alt="base.id" />
							</div>
							<div class="cur-summary-main">
								<div class="cur-summary-title">Valuta di base: {{ base.country }} <span class="cur-code">{{ base.id }}</span></div>
								<div class="cur-summary-sub">I prezzi si scrivono in {{ base.symbol }}; le altre valute attive sono conversioni con il cambio qui sotto.</div>
								<!-- ultimo aggiornamento dei cambi dall'API (Profile.currency_at): cron del lunedi' e salvataggio delle valute -->
								<div class="cur-summary-sub cur-updated" :class="{ 'orange--text text--darken-2': cambiVecchi }" data-tour="updated">
									<v-icon x-small class="mr-1" :color="cambiVecchi ? 'orange darken-2' : ''">mdi-update</v-icon>
									<template v-if="aggiornatiIl">
										Cambi aggiornati il {{ aggiornatiIl | moment('DD/MM/YYYY HH:mm') }}: si aggiornano da soli ogni lunedì e quando salvi le valute.
										<span v-if="cambiVecchi">L’ultimo aggiornamento è di {{ giorniCambi }} giorni fa: il servizio dei cambi potrebbe non rispondere.</span>
									</template>
									<template v-else>I cambi si aggiornano solo con almeno una valuta attiva oltre a quella di base.</template>
								</div>
								<div v-if="esempi.length" class="cur-examples">
									<span class="cur-example-from">{{ importo(100, base) }}</span>
									<v-icon small class="mx-1">mdi-arrow-right</v-icon>
									<span v-for="e in esempi" :key="e.id" class="cur-example">{{ e.testo }}</span>
								</div>
							</div>
						</div>

						<!-- account senza valute (profili nuovi): si creano dall'elenco generale, EUR predefinita -->
						<div v-if="!currencies || !currencies.length" class="text-center pa-8" data-tour="table">
							<div class="grey--text mb-4">Nessuna valuta configurata per questo account.</div>
							<v-btn color="primary" :loading="importando" @click="importaValute"><v-icon left>mdi-download</v-icon>Importa valute</v-btn>
						</div>
						<!-- telefono: ogni valuta diventa una card -->
						<div v-else-if="mobileCards" class="kv-list currency-cards pa-3" data-tour="table">
							<div v-if="!currencies || !currencies.length" class="grey--text text-center py-4">Nessun elemento</div>
							<key-value-card v-for="(item, index) in elenco" :key="`${item.id}`" :fields="campiCard(item)">
								<template v-slot:title>
									<v-avatar size="22px" tile class="mr-2">
										<img :src="bandiera(item.flag)" :alt="item.id" />
									</v-avatar>
									{{ item.country }}
								</template>
								<template v-slot:actions>
									<v-chip v-if="item.default" x-small outlined color="primary" class="mr-1">Predefinita</v-chip>
									<v-menu v-else left eager>
										<template v-slot:activator="{ on, attrs }">
											<v-btn icon small color="primary" v-bind="attrs" v-on="on" :data-tour="index === primaNonDefault ? 'row-menu' : null">
												<v-icon>mdi-dots-vertical</v-icon>
											</v-btn>
										</template>
										<v-list>
											<v-list-item link @click="rendiPredefinita(item)">
												<v-list-item-title class="size9">Rendi predefinita</v-list-item-title>
											</v-list-item>
										</v-list>
									</v-menu>
								</template>
								<div class="d-flex align-center justify-space-between">
									<span class="card-label">Attiva sul sito</span>
									<v-switch :data-tour="index === 0 ? 'switch' : null" v-model="item.is_active" @change="changeStatus(item)" color="primary" class="mt-0 pt-0" hide-details></v-switch>
								</div>
							</key-value-card>
						</div>
						<v-simple-table v-else data-tour="table">
							<template v-slot:default>
								<thead>
									<tr>
										<th style="width: 40px"></th>
										<th class="text-left">Valuta</th>
										<th class="text-left">Simbolo</th>
										<th class="text-left">Cambio</th>
										<th class="text-left">Attiva</th>
										<th class="text-left">Default</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="(item, index) in elenco" :key="`${item.id}`" :class="{ 'cur-row--off': !item.is_active }">
										<td class="pl-0 pr-0" style="width: 40px">
											<v-menu v-if="!item.default" right eager>
												<template v-slot:activator="{ on, attrs }">
													<v-btn icon small color="primary" v-bind="attrs" v-on="on" :data-tour="index === primaNonDefault ? 'row-menu' : null">
														<v-icon>mdi-dots-vertical</v-icon>
													</v-btn>
												</template>
												<v-list>
													<v-list-item link @click="rendiPredefinita(item)">
														<v-list-item-title class="size9">Rendi predefinita</v-list-item-title>
													</v-list-item>
												</v-list>
											</v-menu>
										</td>
										<td>
											<span class="cur-name">
												<img :src="bandiera(item.flag)" :alt="item.id" class="cur-flag" />
												<span class="font-weight-medium">{{ item.country }}</span>
												<span class="cur-code ml-2">{{ item.id }}</span>
											</span>
										</td>
										<td>
											<span class="cur-symbol">{{ item.symbol }}</span>
										</td>
										<td class="cur-rate">
											<span v-if="item.default" class="grey--text">valuta di base</span>
											<span v-else-if="cambio(item)">1 {{ base ? base.symbol : '' }} = {{ numero(cambio(item), 4) }} {{ item.symbol }}</span>
											<span v-else class="grey--text">—</span>
										</td>
										<td>
											<v-switch :data-tour="index === 0 ? 'switch' : null" v-model="item.is_active" @change="changeStatus(item)" color="primary" class="mt-0" hide-details></v-switch>
										</td>
										<td>
											<v-chip v-if="item.default" small outlined color="primary">Predefinita</v-chip>
										</td>
									</tr>
								</tbody>
							</template>
						</v-simple-table>
					</v-card>
					<v-snackbar v-model="snackbar.snackbar" :color="snackbar.color" :multi-line="snackbar.mode === 'multi-line'" :timeout="snackbar.timeout" :vertical="snackbar.mode === 'vertical'">
						{{ snackbar.text }}
						<v-btn dark text @click="snackbar.snackbar = false"> Chiudi </v-btn>
					</v-snackbar>
				</v-layout>
			</v-container>
		</v-main>
		<TourOverlay :tour="tour"></TourOverlay>
	</div>
</template>

<script>
    import moment from 'moment';
    import  axios from 'axios'
    import {mapGetters,mapMutations,mapActions} from 'vuex';
    import TourOverlay from '@/components/core/tour/TourOverlay.vue';
    import KeyValueCard from '@/components/core/KeyValueCard.vue';
    import { useTour } from '@/components/core/tour/useTour';
    import { createCurrencyTourSteps } from './currency-tour';
    /*import SocketIO from 'socket.io-client'
    var socket= SocketIO({
        debug: true,
        connection: 'http://127.0.0.1:4000'
    })*/


    //var socket = io('127.0.0.1:4000')
    //console.log(socket)
    import VueLodash from 'vue-lodash'
    import lodash from 'lodash'
    //import moment from 'vue-moment';




    export default {
        name: "Currencies",
        setup() {
            return { tour: useTour(createCurrencyTourSteps()) };
        },
        beforeDestroy() {
            this.tour.stop();
        },
        data(){
            return {
                items:[],
                setinterval:'',

                snackbar:{
                    snackbar: false,
                    color: '',
                    mode: '',
                    timeout: 6000,
                    text: '',
                },
                // ricerca
                settings:{
                    search:'',
                    filters: {
                        status: [],
                        platform: [],
                        created_at: [],
                        payments: [],
                    },
                    options: {
                        sortBy: ['created_at'],
                        sortDesc: [true],
                        page:1,
                        itemsPerPage:15
                    },
                },

                listSize: [15, 25, 50, 100],
                linkdownload:'',
                searchfield: '',
                selected: [],
                selectedchecbox: [],
                selectedall: [],
                // filters: {
                //     Status: [],
                //     Platform: [],
                //     created_at: [],
                // },
                /* pagination:{
                     sortBy: 'buyer.name',
                     descending: true,
                     rowsPerPage: 15
                 },*/
                //options: {},
                total:0,
                expanded: [],
                singleExpand: false,
                headers: [

                    {text: 'Valuta',      value: ''},

                    {text: 'Codice',  value: ''},
                    {text: 'Simbolo',  value: ''},
                    {text: 'Cambio',  value: ''},
                    {text: 'Status',  value: ''},
                    {text: 'Default',  value: ''},



                ],

                disabled: false,
                absolute: false,
                openOnHover: false,
                value: false,
                closeOnClick: true,
                closeOnContentClick: true,
                offsetX: false,
                offsetY: true,

                dialogconfirm: false,

                isActive : true,

                show1: false,
                messages: {
                    email: [],
                    name: [],
                    password: []

                },


                isMobile: false,
                it:{
                    is_active:'',
                    default:'',
                    type:"it"
                },
                en:{
                    is_active:'',
                    default:'',
                    type:"en"
                },
                de:{
                    is_active:'',
                    default:'',
                    type:"de"
                },
                fr:{
                    is_active:'',
                    default:'',
                    type:"fr"
                },
                es:{
                    is_active:'',
                    default:'',
                    type:"es"
                },
                currencies:[],
                importando: false,
                // ordine di visualizzazione (id), calcolato una volta al caricamento
                ordine: []




            }
        },

        filters: {
            fixed: function (value) {

                if (!+value) return ''
                //  return  value = value.toFixed(2)
                return _.round((value), 2)
                //return  value

            }
        },


        created() {
            this.setPreload(true);


            let items = this.fetchSettings()
            items.then(result => {
                //console.log(result)
                this.currencies= this.getSettings.currencies
                this.ordine = _.sortBy(this.currencies || [], c => (c.default ? 0 : c.is_active ? 1 : 2)).map(c => c.id)
                /*this.fetchCurrencies().then((response) =>{

                    this.currencies= response.data.data
                })*/

               // console.log(result)

                //this.items = result.data.data
                this.setPreload(false);
                /*this.selectedchecbox = [];
                this.selectedall = []

                var it = _.find(result.languages,set => {
                    return set.lang=="it"
                })
                if(it){
                    this.it.is_active=it.is_active
                    this.it.default=it.default
                }
                var en = _.find(result.languages,set => {
                    return set.lang=="en"
                })
                if(en){
                    this.en.is_active=en.is_active
                    this.en.default=en.default
                }
                var de = _.find(result.languages,set => {
                    return set.lang=="de"
                })
                if(de){
                    this.de.is_active=de.is_active
                    this.de.default=de.default
                }
                var fr = _.find(result.languages,set => {
                    return set.lang=="fr"
                })
                if(fr){
                    this.fr.is_active=fr.is_active
                    this.fr.default=fr.default
                }
                var es = _.find(result.languages,set => {
                    return set.lang=="es"
                })
                if(es){
                    this.es.is_active=es.is_active
                    this.es.default=es.default
                }
*/




            })

        },

        watch: {
            selectedLength (val, oldVal) {
                //console.log(val)
                if(val>0){
                    //_.set(this.selectedall,'[0]',1)
                    //_.assign(this.selectedall[0],1)
                    this.selectedall = [-1]

                }
            },

            settings:  {

            },

        },
        components: {
            TourOverlay,
            KeyValueCard,
        },
        computed:{
            // sotto i 769px le righe diventano card
            mobileCards() {
                return this.$vuetify.breakpoint.width < 769;
            },
            // valuta predefinita: i cambi delle altre sono riferiti a lei
            base() {
                return _.find(this.currencies, { default: true }) || null;
            },
            // riga a cui il tutorial aggancia il menu a tre puntini (la predefinita non ce l'ha)
            primaNonDefault() {
                return _.findIndex(this.elenco, c => !c.default);
            },
            // solo vista: prima la predefinita, poi le attive, poi le altre (l'array salvato resta nell'ordine originale)
            // l'ordine si fissa al caricamento: accendendo una valuta la riga non salta
            elenco() {
                if (!this.ordine.length) return this.currencies || [];
                return this.ordine.map(id => _.find(this.currencies, { id })).filter(Boolean);
            },
            // data dell'ultimo aggiornamento dei cambi: conta solo se c'e' almeno una valuta estera attiva
            aggiornatiIl() {
                const attive = _.some(this.currencies, c => c.is_active && !c.default);
                return attive && this.getSettings && this.getSettings.currency_at ? this.getSettings.currency_at : null;
            },
            giorniCambi() {
                return this.aggiornatiIl ? Math.floor((Date.now() - new Date(this.aggiornatiIl).getTime()) / 86400000) : null;
            },
            // il cron gira ogni lunedi': oltre gli 8 giorni un aggiornamento e' saltato
            cambiVecchi() {
                return this.giorniCambi !== null && this.giorniCambi > 8;
            },
            // 100 nella valuta di base convertiti nelle altre valute attive
            esempi() {
                if (!this.base) return [];
                return _.filter(this.currencies, c => c.is_active && !c.default && this.cambio(c)).map(c => ({ id: c.id, testo: this.importo(100 * this.cambio(c), c) }));
            },
            selectedLength() {
                return this.selectedchecbox.length
            },
            ...mapGetters({
                'getAutenticate':'profile/getAutenticate',
                'getToken':'profile/getToken',
                'getId':'profile/getId',
                'getUrl':'settings/getUrl',

                'getDialog':'settings/getDialog',
                'getPreload':'settings/getPreload',
                'getSnackbar':'snackbars/getSnackbar',
                'getColor':'snackbars/getColor',
                'getMultiline':'snackbars/getMultiline',
                'getTimeout':'snackbars/getTimeout',
                'getVertical':'snackbars/getVertical',
                'getText':'snackbars/getText',
                'getSettings':'settings/getSetting',
                'getDrawerFilter':'settings/getDrawerFilter',

            }),
            /*Totale(){
                console.log(filteredItems)
                /!*if(filteredItems.length>0) {
                    return filteredItems.reduce((total, current) => {
                        return total + parseFloat(current.Total)
                    })

                }*!/
            },*/
            navStateFilter:{
                get : function(old,newvalue){

                    return this.getDrawerFilter;
                },
                set : function(newvalue){
                    return this.getDrawerFilter;
                }
            },



        },

        methods: {
            // colonne in sola lettura della riga, per la card da telefono
            campiCard(item) {
                return [
                    { label: 'Codice', value: item.id },
                    { label: 'Simbolo', value: item.symbol },
                    { label: 'Cambio', value: item.default ? 'valuta di base' : this.cambio(item) ? `1 ${this.base ? this.base.symbol : ''} = ${this.numero(this.cambio(item), 4)} ${item.symbol}` : '—', strong: true },
                ];
            },
            cambio(item) {
                const n = parseFloat(item.exchange);
                return Number.isFinite(n) && n > 0 ? n : 0;
            },
            numero(n, decimali = 2) {
                return Number(n).toLocaleString('it-IT', { minimumFractionDigits: decimali, maximumFractionDigits: decimali });
            },
            importo(n, valuta) {
                return `${this.numero(n)} ${valuta.symbol || valuta.id}`;
            },
            bandiera(flag) {
                try {
                    return require(`@/assets/images/flag/${flag}.svg`);
                } catch (e) {
                    return require('@/assets/images/flag/map.svg');
                }
            },
            // crea le valute dell'account dall'elenco generale (solo se non ne ha ancora)
            async importaValute() {
                this.importando = true;
                try {
                    const { data } = await axios.post(`${this.getUrl}currencies/import`, {}, { 'headers': { 'Authorization': `Bearer ${this.getToken}`}});
                    this.setSettings(data.data);
                    this.currencies = data.data.currencies || [];
                    this.ordine = _.sortBy(this.currencies, c => (c.default ? 0 : c.is_active ? 1 : 2)).map(c => c.id);
                    this.snackbar.color = "success";
                    this.snackbar.text = "Valute importate: l'euro è la valuta predefinita, attiva le altre che ti servono";
                } catch (err) {
                    this.snackbar.color = "error";
                    this.snackbar.text = (err.response && err.response.data && err.response.data.message) || err.message;
                    if (err.response && err.response.status === 401) {
                        this.setLogout();
                        this.$router.push('/login');
                    }
                } finally {
                    this.importando = false;
                    this.snackbar.snackbar = true;
                    this.snackbar.mode = 'multi-line';
                    this.snackbar.timeout = 5000;
                }
            },
            // dal menu a tre puntini: stesso effetto della vecchia casella Default
            rendiPredefinita(item) {
                item.default = true;
                this.changeDefault(item);
            },
            ...mapMutations({
                'setLogout': 'profile/setLogout',
                'setDialog': 'settings/setDialog',
                'setPreload': 'settings/setPreload',
                'setSnackbar': 'snackbars/setSnackbar',
                'setClearError': 'settings/setClearError',
                'setMessages': 'settings/setMessages',
                'setExistFilter': 'settings/setExistFilter',
                'setDrawerFilter': 'settings/setDrawerFilter'
            }),
            ...mapActions({
                'fetchSettings': 'settings/asyncFetchData',
                'fetchCurrencies': 'currencies/asyncFetchData',

                'destroyData': 'settings/destroyData',
                'setSettings':'settings/updateData',
                //'resetMessages' : 'settings/resetMessages'
            }),
            async changeDefault(item){

                //console.log(item)
                let urlchange = "currencies/exchange";
                let exchange;
                try {
                    exchange = await axios.post(`${this.getUrl}${urlchange}`,{currency:item.id}, { 'headers': { 'Authorization': `Bearer ${this.getToken}`}})
                } catch (err) {
                    // senza cambi aggiornati non si salva: la valuta torna non predefinita
                    item.default = false;
                    this.snackbar.color="error";
                    this.snackbar.text= "Impossibile leggere i cambi aggiornati: " + err.message;
                    this.snackbar.snackbar = true;
                    this.snackbar.mode = 'multi-line';
                    this.snackbar.timeout=5000;
                    return;
                }
                //console.log("prova",exchange.data.data.rates)
                var rates = exchange.data.data.rates;

                //var check = _.findIndex(exchange.data.data.rates, 'EUR')

                if(item.id=="EUR"){
                    rates["EUR"]=1
                }
                _.forEach(this.currencies, cur => {
                    //console.log(rates[cur.id])

                    cur.exchange=rates[cur.id]
                    if(cur.id==item.id){

                        //cur.default=false
                    }else{
                        cur.default=false
                    }
                })

                let url = "profile/currencies";
                axios.post(`${this.getUrl}${url}`,this.currencies, { 'headers': { 'Authorization': `Bearer ${this.getToken}`}})
                    .then( ({data}) => {
                        //   this.dialog = false;
                        //   this.success = true;
                        //   console.log(data.data);
                        this.setSettings(data.data)
                        this.preload = false;
                        this.snackbar.color="success";
                        this.snackbar.text= "Operazione effettuata con  successo";
                        this.snackbar.snackbar = true;
                        this.snackbar.mode = 'multi-line';
                        this.snackbar.timeout=5000;

                    })
                    .catch(err => {

                        if(err.response) {
                            let errors = err.response.data.error;
                            if (errors) {
                                if(_.isArray(errors)) {
                                    errors.forEach(value => {
                                        //console.log(value.msg);
                                        this.messages[value.param] = value.msg
                                    })
                                }else{
                                    err.message=errors
                                }

                            }


                            this.snackbar.color="error";
                            this.snackbar.text= err.message;
                            this.snackbar.snackbar = true;
                            this.snackbar.mode = 'multi-line';
                            this.snackbar.timeout=5000;
                            this.preload = false;
                            if(err.response.status===401){
                                this.setLogout();
                                this.$router.push('/login')
                            }
                        }
                    });
            },

            changeStatus(item) {
                let url = "profile/currencies";
                axios.post(`${this.getUrl}${url}`,this.currencies, { 'headers': { 'Authorization': `Bearer ${this.getToken}`}})
                    .then( ({data}) => {
                        //   this.dialog = false;
                        //   this.success = true;
                        //   console.log(data.data);
                        this.setSettings(data.data)
                        this.preload = false;
                        this.snackbar.color="success";
                        this.snackbar.text= "Operazione effettuata con  successo";
                        this.snackbar.snackbar = true;
                        this.snackbar.mode = 'multi-line';
                        this.snackbar.timeout=5000;

                    })
                    .catch(err => {

                        if(err.response) {
                            let errors = err.response.data.error;
                            if (errors) {
                                if(_.isArray(errors)) {
                                    errors.forEach(value => {
                                        //console.log(value.msg);
                                        this.messages[value.param] = value.msg
                                    })
                                }else{
                                    err.message=errors
                                }

                            }


                            this.snackbar.color="error";
                            this.snackbar.text= err.message;
                            this.snackbar.snackbar = true;
                            this.snackbar.mode = 'multi-line';
                            this.snackbar.timeout=5000;
                            this.preload = false;
                            if(err.response.status===401){
                                this.setLogout();
                                this.$router.push('/login')
                            }
                        }
                    });

                //console.log(item)
            },
            onResize() {
                if (window.innerWidth < 769)
                    this.isMobile = true;
                else
                    this.isMobile = false;
            },
            toggleDrawerFilter() {

                this.setDrawerFilter(!this.getDrawerFilter);
                this.clippedOrder=0;
                //this.$store.commit('setDrawer') = !this.$store.state.drawer;

            },

            columnValueList(val) {
                return this.getOrders.map(d => d[val])
            },



            async editItem() {
                let el = this.selectedchecbox[0]
                let item = _.find(this.getOrders,i => {
                    return i._id==el
                })

                this.setClearError({});

                this.operation = "FormEdit";
                this.setIndex(this.getOrders.indexOf(item));
                this.setOrder(item);
                this.setMessages();
                this.setDialog(true);
                this.setSnackbar(false);


            },

            toggleClass(num) {

                if (num == 1) {
                    this.active = !this.active;
                }
                if (this.active) {
                    return 'active';
                } else {
                    return 'notActive';
                }

            },
            resetForm() {
                this.form._id = '';
                this.form.name = '';
                this.form.email = '';
                this.form.password = '';
                this.form.action = '';


            },

            searchit:_.debounce(function(){

                if (_.size(this.searchfield) >= 3) {

                    this.settings.search = this.searchfield
                    this.settings.options.page=1


                }else{
                    this.settings.search = ''
                    this.settings.options.page=1
                }
            }, 1000),
            /*async getDataFromApi() {
                this.setPreload(true);
                const {sortBy, sortDesc, page, itemsPerPage} = this.settings.options
                //console.log(this.settings)


                let items = this.fetchOrders(this.settings)

                items.then(result => {
                    //console.log(result);
                    this.total = result.total
                    //this.items = result.data.data
                    this.setPreload(false);




                })

        },*/

            async openNew() {
                this.setClearError({});
                //await this.resetMessages();
                this.setMessages();
                this.setDialog(true);
                this.setSnackbar(false);

            },
            saveSetting(item){
                var payload
                switch(item){
                    case 0:

                        payload = this.mws;
                        this.preload = true;

                     break;
                    case 1:

                        payload = this.ebay;
                        this.preload = true;

                        break;
                }
                let url = "profile/update";
                axios.post(`${this.getUrl}${url}`,payload, { 'headers': { 'Authorization': `Bearer ${this.getToken}`}})
                    .then( ({data}) => {
                        //   this.dialog = false;
                        //   this.success = true;
                        //   console.log(data.data);

                        this.preload = false;
                        this.snackbar.color="success";
                        this.snackbar.text= "Operazione effettuata con  successo";
                        this.snackbar.snackbar = true;
                        this.snackbar.mode = 'multi-line';
                        this.snackbar.timeout=5000;

                    })
                    .catch(err => {

                        if(err.response) {
                            let errors = err.response.data.error;
                            if (errors) {
                                if(_.isArray(errors)) {
                                    errors.forEach(value => {
                                        //console.log(value.msg);
                                        this.messages[value.param] = value.msg
                                    })
                                }else{
                                    err.message=errors
                                }

                            }


                            this.snackbar.color="error";
                            this.snackbar.text= err.message;
                            this.snackbar.snackbar = true;
                            this.snackbar.mode = 'multi-line';
                            this.snackbar.timeout=5000;
                            this.preload = false;
                            if(err.response.status===401){
                                this.setLogout();
                                this.$router.push('/login')
                            }
                        }
                    });




                //console.log(`${this.getUrl}${url}`);


            },
            extension(item){

                var prova =  this.selected.includes(item)
                if(!prova){
                    this.selected.push(item)
                }else{
                    var indice = this.selected.indexOf(item)
                    this.selected.splice(indice,1)
                }

                /*this.selected.push(item)

                    if (!this.getOrders[item].hide) {
                        this.getOrders[item].hide = true;
                    } else  {
                        this.getOrders[item].hide = false;
                    }*/

            },
            checkpresenza(item){
                return this.selected.includes(item)
            },



            getImagePayment(img) {

                let defaultImage = "@/assets/images/Other.svg" // just set default
                let path = `@/assets/images/${img}.svg`



                try {
                    return require(`@/assets/images/${img}.svg`)
                } catch (e) {
                    return require(`@/assets/images/Other.svg`)
                }
            },

            closeSnackbar(){
                this.setSnackbar(false);
            },
            async confirm(){

                let payload = this.form;
                try {
                    this.preload = true;
                    var response = await this.fetchUsers(payload);


                } catch(err) {
                    console.log(err);
                }
                this.preload = false;


            },

            deleteItem (item) {
                this.dialogconfirm=true;

                this.form = item;

            },

        },
        beforeDestroy() {

            this.destroyData()


            //console.log('Main Vue destroyed')
        }
    }
</script>

<style>

</style>

<style scoped>
.card-label {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.6);
}
/* card in lista fuori da v-data-table */
.kv-list > .kv-card + .kv-card {
    margin-top: 8px;
}
/* riepilogo valuta di base */
.cur-summary {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px 16px;
    padding: 12px 16px;
    border: 1px solid #e1e7ef;
    border-radius: 10px;
    background: #f3f6fa;
}
.cur-summary-flag {
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: #fff;
    border: 1px solid #e1e7ef;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}
.cur-summary-flag img {
    width: 28px;
    height: 28px;
    object-fit: contain;
}
.cur-summary-main {
    flex: 1 1 260px;
    min-width: 0;
}
.cur-summary-title {
    font-size: 15px;
    font-weight: 600;
    color: #1f2d3d;
}
.cur-summary-sub {
    font-size: 12px;
    color: #5f6b7a;
}
.cur-updated {
    margin-top: 2px;
}
.cur-examples {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 6px;
    font-size: 13px;
    font-variant-numeric: tabular-nums;
}
.cur-example-from {
    font-weight: 600;
}
.cur-example {
    background: #fff;
    border: 1px solid #e1e7ef;
    border-radius: 6px;
    padding: 1px 8px;
}
/* righe */
.cur-name {
    display: inline-flex;
    align-items: center;
}
.cur-flag {
    width: 22px;
    height: 22px;
    object-fit: contain;
    margin-right: 8px;
}
.cur-code {
    display: inline-block;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    padding: 1px 6px;
    border-radius: 4px;
    border: 1px solid #e1e7ef;
    color: #5f6b7a;
}
.cur-symbol {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 28px;
    padding: 0 6px;
    border-radius: 14px;
    background: #f3f6fa;
    font-weight: 600;
}
.cur-rate {
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
}
.cur-row--off .cur-name,
.cur-row--off .cur-rate {
    opacity: 0.55;
}
</style>
